/**
 * VideoEdit Omni Flash — cut-aware, keyframe-driven video editor recipe.
 *
 * Live mode: hits /api/omni-flash-edit/* (not yet wired on backend — see roadmap).
 * Demo mode (default): runs entirely in-browser with mock fixtures so the
 *                      operator can walk through the full review → process
 *                      → result flow before the Runway endpoints land.
 *
 * Pipeline note: this recipe lives in the Recipes catalogue (slug
 * `videoedit-omni-flash`) and is routed via `custom_route` in routes/recipes.py.
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Film, Upload, Wand2, Layers, Download,
  ArrowRight, ArrowLeft, Check, Loader2, CircleDot, RotateCcw,
  RotateCw, X as XIcon,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const DEMO = false;   // live: /api/omni-flash-edit/* (cut-detect + gpt-image-2 + Omni Flash)
const LS_KEY = "omni_flash_edit_active_job_id";

/* ---- persistence: keep job_id in URL + localStorage so refresh resumes -- */
function readJobIdFromUrl() {
  try {
    const u = new URL(window.location.href);
    return u.searchParams.get("job") || "";
  } catch { return ""; }
}
function writeJobIdToUrl(id) {
  try {
    const u = new URL(window.location.href);
    if (id) u.searchParams.set("job", id); else u.searchParams.delete("job");
    window.history.replaceState({}, "", u.toString());
  } catch { /* noop */ }
}
function stageFromJob(d) {
  if (!d) return "upload";
  if (d.status === "done") return "result";
  if (d.status === "restyled" || d.phase === "awaiting_finalize") return "review";
  if (d.status === "processing") return "processing";
  if (d.status === "ready") return "preview";
  if (d.status === "failed") return "review";
  return "upload";
}

/* ---- house tokens (EiPiCenter) ----------------------------------------- */
const C = {
  bg: "#14181C", panel: "#1B212A", panel2: "#212934", line: "#2B333E",
  lime: "#8FD14F", limeDim: "#5E8F33", text: "#E8ECEF", mute: "#7E8A97",
  amber: "#E8B24F", crimson: "#D1495B",
};
const mono = "'SF Mono','JetBrains Mono',ui-monospace,Menlo,monospace";
const sans = "'Inter',system-ui,-apple-system,Segoe UI,Roboto,sans-serif";

/* ---- demo fixtures ---------------------------------------------------- */
const grad = (i) => {
  const a = (i * 67) % 360, b = (a + 40) % 360;
  return `linear-gradient(135deg, hsl(${a} 45% 28%), hsl(${b} 50% 16%))`;
};
const SAMPLES = {
  short: { duration: 22.0, cuts: [0, 4.2, 9.8, 14.1, 18.6] },
  long:  { duration: 48.0, cuts: [0, 5.1, 9.4, 13.0, 19.7, 24.2, 31.5, 38.0, 43.6] },
};
const buildKeyframes = (s) =>
  s.cuts.map((t, i) => ({
    idx: i, timestamp: t,
    shot_end: i + 1 < s.cuts.length ? s.cuts[i + 1] : s.duration,
    instruction: "",
  }));

/* ---- routing (mirrors the backend exactly) ---------------------------- */
const MAX_S = 30, MAX_CUTS = 10, MAX_KF = 5;
function routing(duration, cuts, edits) {
  const single = duration <= MAX_S && cuts <= MAX_CUTS && edits <= MAX_KF;
  if (single) return { mode: "single", label: "Single pass", reason: "One Omni Flash pass over the whole clip — no trimming, no restitch." };
  const why = duration > MAX_S ? `clip is ${duration.toFixed(0)}s (>30s)`
    : cuts > MAX_CUTS ? `${cuts} cuts (>10)` : `${edits} edits (>5 keyframes)`;
  return { mode: "split", label: "Split + restitch", reason: `${why} — chunking into ≤30s windows at the cuts, then restitching.` };
}
const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

/* ---- trim summary helper (mirrors backend compute_kept_segments) ------ */
function computeTrimSummary(keyframes, duration) {
  const sorted = [...keyframes].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  const deletedRanges = [];
  const keptRanges = [];
  let curStart = null; let curEnd = null;
  for (const k of sorted) {
    if (k.deleted) {
      const s = Number(k.timestamp) || 0;
      const e = Math.min(Number(k.shot_end) || duration, duration);
      if (e > s) deletedRanges.push({ start: s, end: e });
      if (curStart !== null) {
        keptRanges.push({ start: curStart, end: curEnd });
        curStart = null; curEnd = null;
      }
      continue;
    }
    const s = Number(k.timestamp) || 0;
    const e = Math.min(Number(k.shot_end) || duration, duration);
    if (e <= s) continue;
    if (curStart === null) { curStart = s; curEnd = e; }
    else if (Math.abs(s - curEnd) < 0.05) { curEnd = e; }
    else { keptRanges.push({ start: curStart, end: curEnd }); curStart = s; curEnd = e; }
  }
  if (curStart !== null) keptRanges.push({ start: curStart, end: curEnd });
  const trimmedDuration = keptRanges.reduce((a, r) => a + (r.end - r.start), 0);
  const deletedCount = keyframes.filter((k) => k.deleted).length;
  const keptShotCount = keyframes.filter((k) => !k.deleted).length;
  return {
    anyDeleted: deletedCount > 0,
    deletedCount,
    keptShotCount,
    deletedRanges,
    keptRanges,
    trimmedDuration,
  };
}

export default function VideoEditOmniFlashPage() {
  const [stage, setStage] = useState("upload"); // upload | preview | review | processing | result
  const [job, setJob] = useState(null);
  const [proc, setProc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [recent, setRecent] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    document.title = "VideoEdit Omni Flash — Eipicenter";
    const t = timers.current;
    return () => t.forEach(clearTimeout);
  }, []);

  // Load recent runs once for the upload screen history.
  const loadRecent = async () => {
    if (DEMO) return;
    try {
      const r = await fetch(`${API_BASE}/api/omni-flash-edit/recent?limit=10`);
      if (!r.ok) return;
      const d = await r.json();
      setRecent(Array.isArray(d.items) ? d.items : []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadRecent(); }, []);

  // On mount: if URL has ?job=<id> or localStorage has an active id, fetch
  // it and resume on the right stage. Fixes "refresh wipes everything".
  useEffect(() => {
    if (DEMO) return;
    const id = readJobIdFromUrl() || localStorage.getItem(LS_KEY) || "";
    if (!id) return;
    let cancelled = false;
    (async () => {
      setRestoring(true);
      try {
        const r = await fetch(`${API_BASE}/api/omni-flash-edit/${id}`);
        if (!r.ok) {
          // Job vanished — clear stale pointers
          localStorage.removeItem(LS_KEY);
          writeJobIdToUrl("");
          return;
        }
        const d = await r.json();
        if (cancelled) return;
        setJob({
          id: d.id, duration: d.duration, keyframes: d.keyframes || [],
          source_url: d.source_url, width: d.width, height: d.height,
          exceeds_max_duration: d.exceeds_max_duration,
          result_url: d.result_url, status: d.status, phase: d.phase,
          error: d.error,
        });
        const next = stageFromJob(d);
        setStage(next);
        writeJobIdToUrl(d.id);
        localStorage.setItem(LS_KEY, d.id);
        // If still processing, immediately resume the right polling loop.
        if (d.status === "processing") {
          setProc({ pct: d.progress_pct || 0, msg: d.last_message || "Resuming…", error: d.error || "" });
          if (d.phase === "omni") resumePollFinalize(d.id);
          else resumePollRestyle(d.id);
        }
      } catch { /* noop */ }
      finally { if (!cancelled) setRestoring(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist active job_id whenever it changes.
  useEffect(() => {
    if (DEMO) return;
    if (job?.id) {
      localStorage.setItem(LS_KEY, job.id);
      writeJobIdToUrl(job.id);
    }
  }, [job?.id]);

  // Browser back arrow → walk back ONE stage at a time (mirrors the
  // in-app BACK button). We push a sentinel history entry per stage so
  // each popstate consumes one stage. The lastPushedStageRef prevents
  // double-pushes when the same stage re-renders.
  const lastPushedStageRef = useRef(null);
  useEffect(() => {
    if (DEMO) return;
    if (stage !== "upload" && lastPushedStageRef.current !== stage) {
      try {
        window.history.pushState({ omniStage: stage }, "");
        lastPushedStageRef.current = stage;
      } catch { /* noop */ }
    }
    const onPop = () => {
      if (stage === "result") {
        setStage("review");
      } else if (stage === "review" && job?.source_url) {
        setStage("preview");
      } else if (stage === "preview" || stage === "processing" || stage === "review") {
        // Fall through to a hard reset → upload landing.
        timers.current.forEach(clearTimeout);
        timers.current = [];
        setJob(null); setProc(null); setStage("upload");
        lastPushedStageRef.current = null;
        localStorage.removeItem(LS_KEY);
        writeJobIdToUrl("");
        loadRecent();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, job?.source_url]);

  const edits = job ? job.keyframes.filter((k) => k.instruction.trim()).length : 0;
  const route = job ? routing(job.duration, job.keyframes.length - 1, edits) : null;

  async function loadSample(kind) {
    if (!DEMO) return;
    const s = SAMPLES[kind];
    setJob({ duration: s.duration, keyframes: buildKeyframes(s), source_url: null, width: 1920, height: 1080 });
    setStage("preview");
  }

  async function uploadReal(file) {
    if (DEMO) {
      loadSample("short");
      return;
    }
    setUploading(true); setUploadError("");
    try {
      const fd = new FormData();
      fd.append("video", file);
      const r = await fetch(`${API_BASE}/api/omni-flash-edit/upload`, { method: "POST", body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${r.status})`);
      }
      const data = await r.json();
      setJob({
        id: data.id, duration: data.duration, keyframes: data.keyframes,
        source_url: data.source_url, width: data.width, height: data.height,
        exceeds_max_duration: data.exceeds_max_duration,
      });
      setStage("preview");
    } catch (e) {
      setUploadError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  // Resume helpers — separated so the mount-restore path can call them too.
  function resumePollRestyle(jobId) {
    const pollRestyle = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/omni-flash-edit/${jobId}`);
        const d = await r.json();
        setProc({ pct: d.progress_pct || 0, msg: d.last_message || "", error: d.error || "" });
        setJob((j) => j ? {
          ...j,
          keyframes: d.keyframes || j.keyframes,
          phase: d.phase || j.phase,
          status: d.status || j.status,
        } : j);
        if (d.status === "restyled" || d.phase === "awaiting_finalize") {
          setProc(null);
          setStage("review");
          return;
        }
        if (d.status === "failed") {
          setStage("review");
          return;
        }
        if (d.status === "done") {
          setJob((j) => ({ ...(j || {}), result_url: d.result_url }));
          setStage("result");
          return;
        }
        timers.current.push(setTimeout(pollRestyle, 3000));
      } catch (e) {
        timers.current.push(setTimeout(pollRestyle, 5000));
      }
    };
    timers.current.push(setTimeout(pollRestyle, 1500));
  }

  function resumePollFinalize(jobId) {
    const pollFin = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/omni-flash-edit/${jobId}`);
        const d = await r.json();
        setProc({ pct: d.progress_pct || 0, msg: d.last_message || "", error: d.error || "" });
        setJob((j) => j ? { ...j, phase: d.phase || j.phase, status: d.status || j.status } : j);
        if (d.status === "done") {
          setJob((j) => ({ ...(j || {}), result_url: d.result_url }));
          setStage("result");
          return;
        }
        if (d.status === "failed") {
          setStage("review");
          return;
        }
        timers.current.push(setTimeout(pollFin, 4000));
      } catch (e) {
        timers.current.push(setTimeout(pollFin, 6000));
      }
    };
    timers.current.push(setTimeout(pollFin, 2000));
  }

  function setInstruction(idx, text) {
    setJob((j) => ({ ...j, keyframes: j.keyframes.map((k) => (k.idx === idx ? { ...k, instruction: text } : k)) }));
  }

  function setJobKeyframeRef(idx, refUrl) {
    setJob((j) => j ? ({
      ...j,
      keyframes: j.keyframes.map((k) => k.idx === idx ? { ...k, ref_image_url: refUrl } : k),
    }) : j);
  }

  function setJobKeyframeRestyled(idx, restyledUrl) {
    setJob((j) => j ? ({
      ...j,
      keyframes: j.keyframes.map((k) => k.idx === idx ? { ...k, restyled_url: restyledUrl } : k),
    }) : j);
  }

  function setJobKeyframeDeleted(idx, deleted) {
    setJob((j) => j ? ({
      ...j,
      keyframes: j.keyframes.map((k) => k.idx === idx ? { ...k, deleted: !!deleted } : k),
    }) : j);
  }

  async function apply() {
    // Two-phase flow (2026-06-20): hit /restyle-keyframes FIRST so the
    // operator sees the new gpt-image-2 keyframes in the shot cards before
    // paying for Omni Flash. Omni Flash fires only after the operator hits the
    // "Generate video →" button (handleFinalize below).
    if (DEMO) {
      // Demo path: just animate the old flow end-to-end.
      setStage("processing");
      timers.current.push(setTimeout(() => setStage("result"), 2500));
      return;
    }
    const instructions = {};
    job.keyframes.forEach((k) => { if (k.instruction.trim()) instructions[k.idx] = k.instruction.trim(); });
    setStage("processing");
    // Pre-set phase so ProcessingView immediately renders restyle-only
    // milestones (before the first poll comes back from the backend).
    setJob((j) => j ? { ...j, phase: "restyle", status: "processing" } : j);
    setProc({ pct: 5, msg: "Sending edits to gpt-image-2…", error: "" });
    await fetch(`${API_BASE}/api/omni-flash-edit/${job.id}/restyle-keyframes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ instructions }),
    });
    resumePollRestyle(job.id);
  }

  async function handleFinalize() {
    setStage("processing");
    setJob((j) => j ? { ...j, phase: "omni", status: "processing" } : j);
    setProc({ pct: 5, msg: "Sending restyled keyframes to Omni Flash…", error: "" });
    await fetch(`${API_BASE}/api/omni-flash-edit/${job.id}/finalize`, { method: "POST" });
    resumePollFinalize(job.id);
  }

  function reset() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setJob(null); setProc(null); setStage("upload");
    localStorage.removeItem(LS_KEY);
    writeJobIdToUrl("");
    loadRecent();
  }

  async function resumeFromRecent(item) {
    if (!item?.id) return;
    try {
      const r = await fetch(`${API_BASE}/api/omni-flash-edit/${item.id}`);
      if (!r.ok) return;
      const d = await r.json();
      setJob({
        id: d.id, duration: d.duration, keyframes: d.keyframes || [],
        source_url: d.source_url, width: d.width, height: d.height,
        exceeds_max_duration: d.exceeds_max_duration,
        result_url: d.result_url, status: d.status, phase: d.phase, error: d.error,
      });
      setStage(stageFromJob(d));
      writeJobIdToUrl(d.id);
      localStorage.setItem(LS_KEY, d.id);
      if (d.status === "processing") {
        setProc({ pct: d.progress_pct || 0, msg: d.last_message || "Resuming…", error: d.error || "" });
        if (d.phase === "omni") resumePollFinalize(d.id); else resumePollRestyle(d.id);
      }
    } catch { /* noop */ }
  }

  // Stage-aware back navigation — walks back ONE stage at a time so the
  // operator can re-enter the review screen from result (see restyled
  // keyframes again, hit Re-edit or Generate video) or re-confirm the
  // source clip from review.
  //   result     → review    (keyframes preserved · can Re-edit / Generate)
  //   review     → preview   (source clip preview · re-edit shot list)
  //   preview    → upload    (reset + recent runs)
  //   processing → confirm   (job continues server-side) → upload
  //   upload     → exit to /recipes catalogue
  function handleBack() {
    if (stage === "result") {
      setStage("review");
      return { type: "stay" };
    }
    if (stage === "review") {
      if (job?.source_url) { setStage("preview"); return { type: "stay" }; }
      reset();
      return { type: "reset" };
    }
    if (stage === "preview") {
      reset();
      return { type: "reset" };
    }
    if (stage === "processing") {
      const ok = window.confirm(
        "Generation is still running in the background.\n\nLeave this screen anyway?\n\nYour job continues server-side and you can resume it from Recent runs.");
      if (!ok) return { type: "stay" };
      reset();
      return { type: "reset" };
    }
    // stage === "upload"
    return { type: "exit" };
  }

  return (
    <div data-testid="videoedit-omni-flash-page" style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: sans }}>
      <Header stage={stage} onBack={handleBack} />
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 16px 120px" }}>
        {stage === "upload" && <UploadView onSample={loadSample} onFile={uploadReal} uploading={uploading} error={uploadError} recent={recent} onResume={resumeFromRecent} restoring={restoring} />}
        {stage === "preview" && <PreviewView job={job} route={route} onProcess={() => setStage("review")} onReset={reset} />}
        {stage === "review" && (
          <ReviewView job={job} route={route} edits={edits} setInstruction={setInstruction} onFinalize={handleFinalize} setJobKeyframeRef={setJobKeyframeRef} setJobKeyframeRestyled={setJobKeyframeRestyled} setJobKeyframeDeleted={setJobKeyframeDeleted} />
        )}
        {stage === "processing" && <ProcessingView proc={proc} route={route} job={job} />}
        {stage === "result" && <ResultView job={job} onReset={reset} />}
      </div>
      {stage === "review" && (() => {
        const hasRestyled = (job?.keyframes || []).some((k) => k.restyled_url);
        if (hasRestyled) {
          const restyledCount = (job?.keyframes || []).filter((k) => k.restyled_url).length;
          return <FinalizeBar
            onFinalize={handleFinalize}
            onReEdit={apply}
            restyledCount={restyledCount}
            totalShots={(job?.keyframes || []).length}
            edits={edits}
          />;
        }
        return <ApplyBar edits={edits} route={route} onApply={apply} />;
      })()}
    </div>
  );
}

/* ---- header ---------------------------------------------------------- */
function Header({ stage, onBack }) {
  const navigate = useNavigate();
  const goBack = () => {
    const result = onBack ? onBack() : { type: "exit" };
    if (result?.type === "exit") {
      // Always route to the recipes catalogue on explicit EXIT so the user
      // lands somewhere predictable regardless of how they arrived. Using
      // navigate(-1) is fragile (browser-history-length-dependent).
      navigate("/recipes");
    }
  };
  const backLabel = stage && stage !== "upload" ? "BACK" : "EXIT";
  return (
    <div style={{ borderBottom: `1px solid ${C.line}`, padding: "18px 16px", marginBottom: 24 }}>
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 10 }}>
        <button
          data-testid="videoedit-back-btn"
          onClick={goBack}
          aria-label={stage && stage !== "upload" ? "Back to recipe landing" : "Exit to recipes catalogue"}
          title={stage && stage !== "upload" ? "Back to upload + recent runs" : "Exit to recipes catalogue"}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "transparent", border: `1px solid ${C.line}`,
            color: C.mute, borderRadius: 8, padding: "5px 9px 5px 7px",
            cursor: "pointer", fontFamily: mono, fontSize: 11, letterSpacing: 1,
          }}
        >
          <ArrowLeft size={14} /> {backLabel}
        </button>
        <div style={{ width: 26, height: 26, borderRadius: 6, background: C.lime, display: "grid", placeItems: "center" }}>
          <Film size={15} color={C.bg} strokeWidth={2.5} />
        </div>
        <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 3, color: C.mute }}>EIPICENTER</div>
        <div style={{ marginLeft: "auto", fontFamily: mono, fontSize: 11, letterSpacing: 2, color: C.limeDim }}>EDIT ROOM</div>
      </div>
    </div>
  );
}

/* ---- upload ---------------------------------------------------------- */
function UploadView({ onSample, onFile, uploading, error, recent, onResume, restoring }) {
  const inp = useRef(null);
  return (
    <div>
      <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5, margin: "8px 0 6px", lineHeight: 1.1 }}>
        Change the footage<br />you already have.
      </h1>
      <p style={{ color: C.mute, fontSize: 15, margin: "0 0 28px", maxWidth: 460 }}>
        Drop a clip. We find every cut, you describe the change shot by shot, and Omni Flash carries it through — leaving everything else untouched.
      </p>

      {restoring && (
        <div data-testid="videoedit-restoring" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 10, background: C.panel, border: `1px solid ${C.line}`, color: C.mute, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <Loader2 size={14} color={C.lime} style={{ animation: "spin 1s linear infinite" }} />
          Restoring your last session…
        </div>
      )}

      <div
        data-testid="videoedit-upload-dropzone"
        onClick={() => !uploading && inp.current?.click()}
        style={{
          border: `1.5px dashed ${uploading ? C.lime : C.line}`, borderRadius: 14, background: C.panel,
          padding: "40px 20px", textAlign: "center", cursor: uploading ? "wait" : "pointer",
          backgroundImage: `repeating-linear-gradient(90deg, ${C.line} 0 2px, transparent 2px 16px)`,
          backgroundSize: "100% 6px", backgroundRepeat: "no-repeat", backgroundPosition: "0 0",
          opacity: uploading ? 0.85 : 1,
        }}
      >
        <input ref={inp} type="file" accept="video/*" style={{ display: "none" }}
               onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        <div style={{ width: 46, height: 46, borderRadius: 10, background: C.panel2, margin: "0 auto 14px", display: "grid", placeItems: "center" }}>
          {uploading ? <Loader2 size={20} color={C.lime} style={{ animation: "spin 1s linear infinite" }} />
                     : <Upload size={20} color={C.lime} />}
        </div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {uploading ? "Uploading & detecting cuts…" : "Upload a video"}
        </div>
        <div style={{ color: C.mute, fontSize: 13 }}>
          {uploading ? "Hang tight — scene detection runs server-side"
                     : "MP4 · 16:9 / 9:16 / 1:1 · ≤30s ideal · longer clips auto-split"}
        </div>
      </div>

      {error && (
        <div data-testid="videoedit-upload-error" style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, background: "rgba(209,73,91,.12)", border: `1px solid ${C.crimson}`, color: C.crimson, fontSize: 13 }}>
          {error}
        </div>
      )}

      {Array.isArray(recent) && recent.length > 0 && (
        <div data-testid="videoedit-recent" style={{ marginTop: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 10px" }}>
            <span style={{ fontFamily: mono, fontSize: 11, color: C.mute, letterSpacing: 1 }}>RECENT RUNS</span>
            <div style={{ height: 1, flex: 1, background: C.line }} />
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {recent.slice(0, 6).map((r) => (
              <RecentRunRow key={r.id} row={r} onResume={onResume} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RecentRunRow({ row, onResume }) {
  const s = row.status || "ready";
  const phase = row.phase || "";
  const pill = (() => {
    if (s === "done") return { txt: "DONE", color: C.lime, bg: "rgba(143,209,79,.14)" };
    if (s === "failed") return { txt: row.error ? "FAILED" : "CANCELLED", color: C.crimson, bg: "rgba(209,73,91,.14)" };
    if (s === "processing") return { txt: phase === "omni" ? `OMNI ${row.progress_pct || 0}%` : `RESTYLE ${row.progress_pct || 0}%`, color: C.amber, bg: "rgba(232,178,79,.14)" };
    if (s === "restyled") return { txt: "REVIEW", color: C.amber, bg: "rgba(232,178,79,.14)" };
    return { txt: "READY", color: C.mute, bg: "transparent" };
  })();
  const created = row.created_at ? new Date(row.created_at) : null;
  const ago = created ? timeAgo(created) : "";
  const thumbBg = row.thumb_url
    ? `url("${row.thumb_url}") center/cover no-repeat, ${C.bg}`
    : grad((row.duration || 1) | 0);
  return (
    <button
      data-testid={`videoedit-recent-row-${row.id}`}
      onClick={() => onResume(row)}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
        background: C.panel, border: `1px solid ${C.line}`, borderRadius: 12, padding: "10px 12px",
        color: C.text, cursor: "pointer",
      }}>
      <div style={{ width: 54, height: 36, borderRadius: 6, background: thumbBg, flexShrink: 0, position: "relative" }}>
        {s === "done" && row.result_url && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,.35)", borderRadius: 6 }}>
            <Check size={14} color={C.lime} strokeWidth={3} />
          </div>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: mono, fontSize: 11, color: C.mute, letterSpacing: 1 }}>
          {row.duration ? `${row.duration.toFixed(1)}s` : "—"} · {row.cuts || 0} cuts · {row.edits_count || 0} edits
        </div>
        <div style={{ fontSize: 12.5, color: C.text, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.last_message || row.error || "—"}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <span style={{ fontFamily: mono, fontSize: 10, letterSpacing: 0.5, padding: "2px 8px", borderRadius: 999, color: pill.color, background: pill.bg, border: `1px solid ${pill.color}33`, whiteSpace: "nowrap" }}>
          {pill.txt}
        </span>
        <span style={{ fontFamily: mono, fontSize: 10, color: C.mute }}>{ago}</span>
      </div>
    </button>
  );
}

function timeAgo(d) {
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s | 0}s ago`;
  if (s < 3600) return `${(s / 60) | 0}m ago`;
  if (s < 86400) return `${(s / 3600) | 0}h ago`;
  return `${(s / 86400) | 0}d ago`;
}

/* ---- preview (NEW — sits between upload and review) ----------------- */
function PreviewView({ job, route, onProcess, onReset }) {
  const cuts = (job.keyframes?.length || 1) - 1;
  // Figure out a sensible aspect for the <video> slot from probed width/height.
  const aspect = (job.width && job.height) ? `${job.width} / ${job.height}` : "16 / 9";
  return (
    <div data-testid="videoedit-preview-view">
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: C.lime, marginBottom: 8 }}>
        STEP 1 OF 2 — VIDEO UPLOADED
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 800, margin: "0 0 6px" }}>
        Ready to process
      </h2>
      <p style={{ color: C.mute, fontSize: 14, margin: "0 0 22px" }}>
        We detected the cuts. Next: write what you want changed in each shot — Omni Flash carries the change through the rest of the footage. <b style={{ color: C.text }}>No edit happens yet</b> — processing starts only after you click <b style={{ color: C.text }}>Apply edits</b> at the bottom of the next screen.
      </p>

      <div style={{
        background: C.panel, borderRadius: 14, border: `1px solid ${C.line}`,
        overflow: "hidden", marginBottom: 18,
      }}>
        <div style={{ aspectRatio: aspect, background: "#000", position: "relative" }}>
          {job.source_url ? (
            <video
              data-testid="videoedit-preview-video"
              src={job.source_url}
              controls
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
            />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: C.mute, fontFamily: mono, fontSize: 12 }}>
              demo · no actual video file
            </div>
          )}
        </div>
        <div style={{ padding: "12px 14px", display: "flex", flexWrap: "wrap", gap: 14, fontFamily: mono, fontSize: 12, color: C.mute, borderTop: `1px solid ${C.line}` }}>
          <span><b style={{ color: C.text }}>{job.duration?.toFixed(1)}s</b> duration</span>
          <span><b style={{ color: C.text }}>{cuts}</b> cut{cuts === 1 ? "" : "s"}</span>
          {job.width && job.height && (
            <span><b style={{ color: C.text }}>{job.width}×{job.height}</b></span>
          )}
          <span style={{ color: route?.mode === "split" ? C.amber : C.lime }}>
            → {route?.label || "Single pass"}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button
          data-testid="videoedit-process-btn"
          onClick={onProcess}
          style={{
            flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 8,
            background: C.lime, color: C.bg, border: "none", borderRadius: 10,
            padding: "14px", fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}>
          Describe edits → <ArrowRight size={16} />
        </button>
        <button
          data-testid="videoedit-preview-reset"
          onClick={onReset}
          style={{
            display: "flex", alignItems: "center", gap: 8, background: C.panel,
            color: C.text, border: `1px solid ${C.line}`, borderRadius: 10,
            padding: "14px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer",
          }}>
          <RotateCcw size={15} /> Replace
        </button>
      </div>
    </div>
  );
}
function SampleBtn({ onClick, title, meta, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
      background: C.panel, border: `1px solid ${C.line}`, borderRadius: 12, padding: "14px 16px",
      color: C.text, cursor: "pointer",
    }}>
      <Film size={18} color={C.mute} />
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
        <div style={{ color: C.mute, fontSize: 12, fontFamily: mono }}>{meta}</div>
      </div>
      <ArrowRight size={16} color={C.lime} style={{ marginLeft: "auto" }} />
    </button>
  );
}

/* ---- review ---------------------------------------------------------- */
function ReviewView({ job, route, edits, setInstruction, onFinalize, setJobKeyframeRef, setJobKeyframeRestyled, setJobKeyframeDeleted }) {
  const kfs = job.keyframes;
  const hasRestyled = kfs.some((k) => k.restyled_url);
  const failedNote = job.error ? job.error : "";
  // Lightbox state — clicking any thumbnail expands it full-screen so the
  // operator can scrutinise the gpt-image-2 result before approving.
  const [lightbox, setLightbox] = useState(null); // { url, label }

  // Operator-trim summary: how many shots survive after deletions, and
  // what the trimmed video duration will be. Mirrors the backend's
  // `compute_kept_segments` math so the UI reflects exactly what Omni Flash
  // will see at finalize time.
  const trim = computeTrimSummary(kfs, job.duration);
  return (
    <div>
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: C.lime, marginBottom: 8 }}>
        {hasRestyled ? "REVIEW RESTYLED KEYFRAMES" : "STEP 2 OF 2 — DESCRIBE THE EDITS"}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 6px" }}>
        {hasRestyled ? "Approve the new keyframes" : "What should change in each shot?"}
      </h2>
      <p style={{ color: C.mute, fontSize: 13, margin: "0 0 18px", maxWidth: 560 }}>
        {hasRestyled
          ? <>Each shot below shows the original on the left and the gpt-image-2 restyle on the right. When the keyframes look right, hit <b style={{ color: C.text }}>Generate video</b> below — Omni Flash will carry these changes through the full clip.</>
          : <>Type into any shot below — leave others blank to keep them untouched. When ready, hit <b style={{ color: C.text }}>Apply edits</b> at the bottom.</>}
      </p>
      {failedNote && (
        <div data-testid="videoedit-review-error" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(209,73,91,.12)", border: `1px solid ${C.crimson}`, color: C.crimson, fontSize: 13 }}>
          {failedNote}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <Stat label="DURATION" value={trim.anyDeleted
          ? `${trim.trimmedDuration.toFixed(1)}s`
          : `${job.duration.toFixed(1)}s`}
          accent={trim.anyDeleted} />
        <Stat label="CUTS" value={trim.anyDeleted
          ? Math.max(0, trim.keptShotCount - 1)
          : kfs.length - 1}
          accent={trim.anyDeleted} />
        <Stat label="EDITS QUEUED" value={edits} accent={edits > 0} />
        <RoutePill route={route} />
        {trim.anyDeleted && (
          <div data-testid="videoedit-trim-summary" style={{
            fontFamily: mono, fontSize: 11, letterSpacing: 1, color: C.lime,
            background: "rgba(168,229,73,.08)", border: `1px solid ${C.limeDim}`,
            borderRadius: 8, padding: "6px 10px",
          }}>
            {trim.deletedCount} TRIMMED · −{(job.duration - trim.trimmedDuration).toFixed(1)}s
          </div>
        )}
      </div>

      <div style={{ position: "relative", height: 40, background: C.panel, borderRadius: 10, border: `1px solid ${C.line}`, marginBottom: 24, overflow: "hidden" }}>
        {kfs.map((k) => {
          const left = (k.timestamp / job.duration) * 100;
          const edited = (k.instruction || "").trim();
          const isDeleted = !!k.deleted;
          return (
            <div key={k.idx} style={{ position: "absolute", left: `${left}%`, top: 0, bottom: 0, opacity: isDeleted ? 0.3 : 1 }}>
              <div style={{ width: 2, height: "100%", background: isDeleted ? C.crimson : (edited ? C.lime : C.line), textDecoration: isDeleted ? "line-through" : "none" }} />
              <div style={{ position: "absolute", top: 4, left: 4, fontFamily: mono, fontSize: 9, color: isDeleted ? C.crimson : (edited ? C.lime : C.mute), textDecoration: isDeleted ? "line-through" : "none" }}>
                {fmt(k.timestamp)}
              </div>
            </div>
          );
        })}
        {/* Highlight trimmed-out ranges so the operator can scan
            at-a-glance what's getting removed. */}
        {trim.deletedRanges.map((r, i) => (
          <div key={`trim-${i}`} style={{
            position: "absolute",
            left: `${(r.start / job.duration) * 100}%`,
            width: `${((r.end - r.start) / job.duration) * 100}%`,
            top: 0, bottom: 0,
            background: "repeating-linear-gradient(45deg, rgba(209,73,91,.18) 0 8px, rgba(209,73,91,.06) 8px 16px)",
            borderLeft: `1px dashed ${C.crimson}`, borderRight: `1px dashed ${C.crimson}`,
            pointerEvents: "none",
          }} />
        ))}
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {kfs.map((k) => (
          <ShotCard key={k.idx} k={k}
            onChange={(t) => setInstruction(k.idx, t)}
            restyled={hasRestyled}
            jobId={job.id}
            onExpand={(url, label) => setLightbox({ url, label })}
            onRefUploaded={(idx, url) => setJobKeyframeRef && setJobKeyframeRef(idx, url)}
            onRestyled={(idx, url) => setJobKeyframeRestyled && setJobKeyframeRestyled(idx, url)}
            onDeleted={(idx, del) => setJobKeyframeDeleted && setJobKeyframeDeleted(idx, del)} />
        ))}
      </div>

      {lightbox && (
        <Lightbox url={lightbox.url} label={lightbox.label}
          onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

function Lightbox({ url, label, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      data-testid="shot-lightbox"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(8,10,12,.92)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, zIndex: 1000, cursor: "zoom-out",
      }}
    >
      <button
        data-testid="shot-lightbox-close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        style={{
          position: "absolute", top: 18, right: 18, background: "rgba(20,24,28,.85)",
          border: `1px solid ${C.line}`, color: C.text, borderRadius: 999,
          width: 38, height: 38, display: "flex", alignItems: "center",
          justifyContent: "center", cursor: "pointer",
        }}
      >
        <XIcon size={18} />
      </button>
      {label && (
        <div style={{
          position: "absolute", top: 22, left: 22, fontFamily: mono,
          fontSize: 11, letterSpacing: 2, color: C.lime,
          background: "rgba(20,24,28,.7)", padding: "5px 10px", borderRadius: 6,
          border: `1px solid ${C.limeDim}`,
        }}>
          {label}
        </div>
      )}
      <img src={url} alt={label || "expanded"}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "92vw", maxHeight: "88vh", objectFit: "contain",
          borderRadius: 12, border: `1px solid ${C.line}`,
          boxShadow: "0 30px 80px rgba(0,0,0,.6)",
        }} />
    </div>
  );
}

function ShotCard({ k, onChange, restyled, jobId, onRefUploaded, onExpand, onRestyled, onDeleted }) {
  const active = (k.instruction || "").trim().length > 0;
  const isDeleted = !!k.deleted;
  const refInp = useRef(null);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [refErr, setRefErr] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [regenErr, setRegenErr] = useState("");
  const [togglingDelete, setTogglingDelete] = useState(false);
  const [delErr, setDelErr] = useState("");

  async function pickRef(file) {
    if (!file || !jobId) return;
    setUploadingRef(true); setRefErr("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${API_BASE}/api/omni-flash-edit/${jobId}/shots/${k.idx}/ref-image`,
        { method: "POST", body: fd });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Upload failed (${r.status})`);
      }
      const d = await r.json();
      onRefUploaded && onRefUploaded(k.idx, d.ref_image_url);
    } catch (e) {
      setRefErr(e.message || "Upload failed");
    } finally {
      setUploadingRef(false);
    }
  }

  async function clearRef() {
    if (!jobId) return;
    try {
      await fetch(`${API_BASE}/api/omni-flash-edit/${jobId}/shots/${k.idx}/ref-image`,
        { method: "DELETE" });
      onRefUploaded && onRefUploaded(k.idx, "");
    } catch { /* noop */ }
  }

  async function regenerateOne() {
    if (!jobId || regenerating) return;
    setRegenerating(true); setRegenErr("");
    try {
      const r = await fetch(
        `${API_BASE}/api/omni-flash-edit/${jobId}/shots/${k.idx}/restyle`,
        { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}) });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Regen failed (${r.status})`);
      }
      const d = await r.json();
      onRestyled && onRestyled(k.idx, d.restyled_url);
    } catch (e) {
      setRegenErr(e.message || "Regen failed");
    } finally {
      setRegenerating(false);
    }
  }

  async function toggleDelete() {
    if (!jobId || togglingDelete) return;
    setTogglingDelete(true); setDelErr("");
    const next = !isDeleted;
    try {
      const r = await fetch(
        `${API_BASE}/api/omni-flash-edit/${jobId}/shots/${k.idx}/delete`,
        { method: next ? "POST" : "DELETE" });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Trim failed (${r.status})`);
      }
      onDeleted && onDeleted(k.idx, next);
    } catch (e) {
      setDelErr(e.message || "Trim failed");
    } finally {
      setTogglingDelete(false);
    }
  }

  // Prefer the real S3 thumbnail extracted at upload time; fall back to the
  // colored gradient when the backend didn't return one (DEMO mode or thumb
  // extraction failed).
  const thumbBg = k.thumb_url
    ? `url("${k.thumb_url}") center/cover no-repeat, ${C.bg}`
    : grad(k.idx);
  const restyledBg = k.restyled_url
    ? `url("${k.restyled_url}") center/cover no-repeat, ${C.bg}`
    : null;
  const refBg = k.ref_image_url
    ? `url("${k.ref_image_url}") center/cover no-repeat, ${C.bg}`
    : null;
  return (
    <div style={{
      position: "relative",
      display: "flex", gap: 12, background: C.panel, borderRadius: 12,
      border: `1px solid ${isDeleted ? C.crimson : (active ? C.limeDim : C.line)}`,
      padding: 12,
      opacity: isDeleted ? 0.55 : 1,
      transition: "opacity 160ms ease",
    }}>
      <div
        data-testid={`shot-thumb-${k.idx}`}
        onClick={() => k.thumb_url && onExpand && onExpand(k.thumb_url, `SHOT ${String(k.idx + 1).padStart(2, "0")} · ORIGINAL`)}
        title={k.thumb_url ? "Click to expand" : ""}
        style={{ flexShrink: 0, width: 92, height: 64, borderRadius: 8, background: thumbBg, position: "relative", overflow: "hidden", cursor: k.thumb_url ? "zoom-in" : "default", filter: isDeleted ? "grayscale(.85)" : "none" }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)" }} />
        <div style={{ position: "absolute", left: 6, top: 6, fontFamily: mono, fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,.95)", textShadow: "0 1px 2px rgba(0,0,0,.6)" }}>
          {String(k.idx + 1).padStart(2, "0")}
        </div>
        <div style={{ position: "absolute", right: 6, bottom: 5, fontFamily: mono, fontSize: 9, color: "rgba(255,255,255,.9)", textShadow: "0 1px 2px rgba(0,0,0,.7)" }}>
          {fmt(k.timestamp)}–{fmt(k.shot_end)}
        </div>
        {/* Trim toggle — × to remove this shot from the final video,
            ↺ to restore. Lives on the ORIGINAL thumbnail so it's
            visible even before keyframes are restyled. */}
        <button
          data-testid={`shot-trim-${k.idx}`}
          onClick={(e) => { e.stopPropagation(); toggleDelete(); }}
          disabled={togglingDelete}
          title={isDeleted ? "Restore this shot" : "Trim this shot OUT of the final video"}
          aria-label={isDeleted ? "Restore this shot" : "Trim this shot"}
          style={{
            position: "absolute", right: 4, top: 4,
            width: 22, height: 22, borderRadius: 999,
            background: isDeleted ? "rgba(143,209,79,.85)" : "rgba(20,24,28,.82)",
            border: `1px solid ${isDeleted ? C.lime : C.crimson}`,
            color: isDeleted ? "#14181C" : C.crimson,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: togglingDelete ? "wait" : "pointer", padding: 0,
          }}
        >
          {togglingDelete
            ? <Loader2 size={12} className="spin" />
            : isDeleted ? <RotateCcw size={12} /> : <XIcon size={12} />}
        </button>
      </div>
      {restyled && restyledBg && (
        <div
          data-testid={`shot-restyled-${k.idx}`}
          onClick={() => !regenerating && onExpand && onExpand(k.restyled_url, `SHOT ${String(k.idx + 1).padStart(2, "0")} · RESTYLED`)}
          title="Click to expand · ↻ to regenerate just this shot"
          style={{ flexShrink: 0, width: 92, height: 64, borderRadius: 8, background: restyledBg, position: "relative", overflow: "hidden", border: `1px solid ${C.limeDim}`, cursor: regenerating ? "wait" : "zoom-in" }}>
          <div style={{ position: "absolute", left: 4, top: 4, fontFamily: mono, fontSize: 9, fontWeight: 700, color: C.lime, background: "rgba(20,24,28,.7)", padding: "1px 5px", borderRadius: 4 }}>NEW</div>
          <button
            data-testid={`shot-regen-${k.idx}`}
            onClick={(e) => { e.stopPropagation(); regenerateOne(); }}
            disabled={regenerating || !active}
            title={active ? "Regenerate just this shot" : "Type an instruction first"}
            aria-label="Regenerate this shot"
            style={{
              position: "absolute", right: 4, top: 4,
              width: 22, height: 22, borderRadius: 999,
              background: "rgba(20,24,28,.82)", border: `1px solid ${C.limeDim}`,
              color: regenerating ? C.mute : C.lime,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: regenerating || !active ? "not-allowed" : "pointer",
              padding: 0,
            }}
          >
            {regenerating
              ? <Loader2 size={12} className="spin" />
              : <RotateCw size={12} />}
          </button>
          {regenerating && (
            <div style={{
              position: "absolute", inset: 0,
              background: "rgba(8,10,12,.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: mono, fontSize: 9, letterSpacing: 1, color: C.lime,
            }}>
              REGEN…
            </div>
          )}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontFamily: mono, fontSize: 11, color: C.mute, letterSpacing: 1, textDecoration: isDeleted ? "line-through" : "none" }}>SHOT {String(k.idx + 1).padStart(2, "0")}</span>
          {isDeleted ? (
            <span data-testid={`shot-trimmed-badge-${k.idx}`} style={{ fontFamily: mono, fontSize: 9, color: C.crimson, letterSpacing: 1, padding: "1px 6px", borderRadius: 4, border: `1px solid ${C.crimson}`, background: "rgba(209,73,91,.12)" }}>
              TRIMMED
            </span>
          ) : (
            <Chip active={active} />
          )}
          {!isDeleted && k.ref_image_url && (
            <span style={{ fontFamily: mono, fontSize: 9, color: C.lime, letterSpacing: 0.5, padding: "1px 6px", borderRadius: 4, border: `1px solid ${C.limeDim}` }}>
              REF
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          {/* Optional reference image slot — operator drops in a sample of
              what they want (e.g. a swatch of the exact red). Passed to
              gpt-image-2 as image-2 on the next restyle run. */}
          <RefSlot
            idx={k.idx}
            refBg={refBg}
            uploading={uploadingRef}
            disabled={!jobId}
            onPick={() => refInp.current?.click()}
            onClear={clearRef}
          />
          <input ref={refInp} type="file" accept="image/png,image/jpeg,image/webp"
                 style={{ display: "none" }}
                 onChange={(e) => e.target.files?.[0] && pickRef(e.target.files[0])} />
          <textarea
            data-testid={`shot-instruction-${k.idx}`}
            value={k.instruction || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={k.ref_image_url
              ? "Describe the change — uploaded ref will guide gpt-image-2"
              : "Describe the change — name only what changes…"}
            rows={2}
            style={{
              flex: 1, resize: "vertical", background: C.bg, color: C.text,
              border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 10px",
              fontFamily: sans, fontSize: 13, lineHeight: 1.4, outline: "none", boxSizing: "border-box",
            }}
          />
        </div>
        {refErr && (
          <div data-testid={`shot-ref-err-${k.idx}`} style={{ marginTop: 6, color: C.crimson, fontSize: 11 }}>
            {refErr}
          </div>
        )}
        {regenErr && (
          <div data-testid={`shot-regen-err-${k.idx}`} style={{ marginTop: 6, color: C.crimson, fontSize: 11 }}>
            {regenErr}
          </div>
        )}
        {delErr && (
          <div data-testid={`shot-trim-err-${k.idx}`} style={{ marginTop: 6, color: C.crimson, fontSize: 11 }}>
            {delErr}
          </div>
        )}
      </div>
    </div>
  );
}

function RefSlot({ idx, refBg, uploading, disabled, onPick, onClear }) {
  if (refBg) {
    return (
      <div
        data-testid={`shot-ref-thumb-${idx}`}
        title="Click × to remove · this image guides gpt-image-2"
        style={{
          position: "relative", flexShrink: 0, width: 56, height: 56,
          borderRadius: 8, background: refBg, border: `1px solid ${C.limeDim}`,
          overflow: "hidden",
        }}
      >
        <button
          data-testid={`shot-ref-clear-${idx}`}
          onClick={onClear}
          aria-label="Remove reference image"
          style={{
            position: "absolute", top: -6, right: -6, width: 20, height: 20,
            borderRadius: 999, background: C.bg, border: `1px solid ${C.line}`,
            color: C.text, fontSize: 12, lineHeight: 1, cursor: "pointer",
            display: "grid", placeItems: "center",
          }}
        >
          ×
        </button>
      </div>
    );
  }
  return (
    <button
      data-testid={`shot-ref-add-${idx}`}
      onClick={onPick}
      disabled={disabled || uploading}
      title={disabled
        ? "Upload the source video first"
        : "Optional: upload a reference image · passed to gpt-image-2 alongside your text"}
      style={{
        flexShrink: 0, width: 56, height: 56, borderRadius: 8,
        background: C.bg, border: `1px dashed ${C.line}`,
        color: C.mute, cursor: disabled || uploading ? "not-allowed" : "pointer",
        display: "grid", placeItems: "center", gap: 2,
      }}
    >
      {uploading
        ? <Loader2 size={14} color={C.lime} style={{ animation: "spin 1s linear infinite" }} />
        : <>
            <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 300 }}>+</span>
            <span style={{ fontFamily: mono, fontSize: 8.5, letterSpacing: 0.5 }}>REF</span>
          </>}
    </button>
  );
}

function Chip({ active }) {
  return (
    <span style={{
      fontFamily: mono, fontSize: 10, letterSpacing: 0.5, padding: "2px 7px", borderRadius: 999,
      color: active ? C.bg : C.mute, background: active ? C.lime : "transparent",
      border: `1px solid ${active ? C.lime : C.line}`,
    }}>
      {active ? "QUEUED" : "UNTOUCHED"}
    </span>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, padding: "8px 12px", minWidth: 78 }}>
      <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: 1, color: C.mute }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent ? C.lime : C.text }}>{value}</div>
    </div>
  );
}
function RoutePill({ route }) {
  const split = route.mode === "split";
  return (
    <div style={{
      flex: "1 1 220px", minWidth: 200, display: "flex", gap: 9, alignItems: "flex-start",
      background: C.panel, borderRadius: 10, padding: "9px 12px",
      border: `1px solid ${split ? C.amber : C.limeDim}`,
    }}>
      {split ? <Layers size={15} color={C.amber} style={{ marginTop: 1 }} /> : <Wand2 size={15} color={C.lime} style={{ marginTop: 1 }} />}
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, color: split ? C.amber : C.lime }}>{route.label}</div>
        <div style={{ color: C.mute, fontSize: 11.5, lineHeight: 1.35 }}>{route.reason}</div>
      </div>
    </div>
  );
}

/* ---- apply bar ------------------------------------------------------- */
function ApplyBar({ edits, route, onApply }) {
  const disabled = edits === 0;
  return (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0, borderTop: `1px solid ${C.line}`,
      background: "rgba(20,24,28,.92)", backdropFilter: "blur(8px)", padding: "12px 16px",
    }}>
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 13, color: C.mute }}>
          <b style={{ color: edits ? C.lime : C.mute }}>{edits}</b> edit{edits === 1 ? "" : "s"} queued · {route.label}
        </div>
        <button
          data-testid="videoedit-apply-btn"
          onClick={onApply}
          disabled={disabled}
          style={{
            marginLeft: "auto", display: "flex", alignItems: "center", gap: 8,
            background: disabled ? C.panel2 : C.lime, color: disabled ? C.mute : C.bg,
            border: "none", borderRadius: 10, padding: "11px 20px", fontWeight: 700, fontSize: 14,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          Apply edits <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ---- finalize bar — appears after restyle, before Omni Flash fires --------- */
function FinalizeBar({ onFinalize, onReEdit, restyledCount, totalShots, edits }) {
  return (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0, borderTop: `1px solid ${C.line}`,
      background: "rgba(20,24,28,.92)", backdropFilter: "blur(8px)", padding: "12px 16px",
    }}>
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: C.mute, flex: "1 1 180px", minWidth: 0 }}>
          <b style={{ color: C.lime }}>{restyledCount}</b>/{totalShots} keyframes restyled
        </div>
        <button
          data-testid="videoedit-reedit-btn"
          onClick={onReEdit}
          disabled={!edits}
          title={edits ? "Re-run gpt-image-2 with current instructions" : "Edit a shot instruction first"}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            background: C.panel, color: edits ? C.text : C.mute,
            border: `1px solid ${C.line}`, borderRadius: 10,
            padding: "11px 16px", fontWeight: 600, fontSize: 13,
            cursor: edits ? "pointer" : "not-allowed",
          }}
        >
          <RotateCcw size={14} /> Re-edit
        </button>
        <button
          data-testid="videoedit-finalize-btn"
          onClick={onFinalize}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            background: C.lime, color: C.bg,
            border: "none", borderRadius: 10, padding: "11px 20px", fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}
        >
          Generate video <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ---- processing ------------------------------------------------------ */
function ProcessingView({ proc, route, job }) {
  // Live mode (non-DEMO): backend pushes {pct, msg, error}. Render a
  // single progress bar + the streamed `last_message` so the operator
  // can see exactly what's happening (Restyling keyframes 2/3, Omni Flash
  // window 1/2, Restitching, etc.).
  const isLive = proc && (typeof proc.pct === "number" || typeof proc.msg === "string" || proc.error !== undefined);
  if (isLive) {
    const pct = Math.max(0, Math.min(100, proc.pct || 0));
    const err = proc.error || "";
    // Phase is the single source of truth for which milestones to show.
    // - "restyle"  → only restyle milestones + the review-gate callout
    // - "omni"    → only Omni + storing milestones
    // - missing    → infer from the streamed message text
    const phase = job?.phase
      || (/restyl/i.test(proc.msg || "") ? "restyle"
        : /omni|window|propag|restitch|storing/i.test(proc.msg || "") ? "omni"
        : "restyle");
    const isRestyle = phase === "restyle";
    const isOmni = phase === "omni";

    const phaseLabel = isRestyle
      ? "Phase 1 of 2 · Restyling keyframes"
      : "Phase 2 of 2 · Generating video";
    const phaseCopy = err
      ? "Something went wrong. Check the error below — most failures are recoverable by hitting Apply edits again or replacing the source clip."
      : isRestyle
        ? "gpt-image-2 is restyling each edited keyframe. Omni Flash will NOT fire automatically — you'll review the new keyframes first and approve them."
        : "You approved the new keyframes. Omni Flash is now propagating the change through the source footage. Leave this open — it updates as each window completes.";

    return (
      <div data-testid="videoedit-processing-live" style={{ paddingTop: 30 }}>
        <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: err ? C.crimson : C.lime, marginBottom: 8 }}>
          {err ? "FAILED" : phaseLabel.toUpperCase()}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 6px" }}>
          {err ? "Pipeline failed" : (isRestyle ? "Restyling keyframes…" : `${route?.label || "Omni Flash"} in progress`)}
        </h2>
        <p style={{ color: C.mute, fontSize: 13, margin: "0 0 22px" }}>{phaseCopy}</p>

        {/* progress bar */}
        <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, padding: "16px 18px", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontFamily: mono, fontSize: 11, color: C.mute }}>
            <span data-testid="videoedit-progress-msg">{proc.msg || "Starting…"}</span>
            <span data-testid="videoedit-progress-pct" style={{ color: err ? C.crimson : C.lime }}>{pct}%</span>
          </div>
          <div style={{ height: 6, background: C.bg, borderRadius: 999, overflow: "hidden" }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: err ? C.crimson : C.lime,
              transition: "width .5s ease-out",
            }} />
          </div>
        </div>

        {/* phase-aware milestone strip — only show what's relevant to the
            current phase. NEVER show Omni milestones during restyle, or
            the operator panics thinking Omni Flash is already firing. */}
        <div style={{ display: "grid", gap: 2 }}>
          {isRestyle && (
            <>
              <MilestoneRow state="done" label="Cuts detected · uploaded" />
              <MilestoneRow state={pct >= 100 ? "done" : "run"} label="Restyling keyframes via gpt-image-2" />
            </>
          )}
          {isOmni && (
            <>
              <MilestoneRow state="done" label="Cuts detected · uploaded" />
              <MilestoneRow state="done" label="Keyframes restyled & approved" />
              <MilestoneRow
                state={pct >= 90 ? "done" : "run"}
                label={route?.mode === "split" ? "Omni Flash per window · restitching" : "Propagating with Omni Flash"} />
              <MilestoneRow state={pct >= 100 ? "done" : "wait"} label="Storing result" />
            </>
          )}
        </div>

        {/* Review-gate callout during restyle — drives home that Omni Flash
            will NOT fire until the operator clicks Generate video. */}
        {isRestyle && !err && (
          <div data-testid="videoedit-review-gate-callout" style={{
            marginTop: 16, padding: "14px 16px", borderRadius: 12,
            background: "rgba(143,209,79,.08)", border: `1px dashed ${C.limeDim}`,
            display: "flex", gap: 12, alignItems: "flex-start",
          }}>
            <Wand2 size={18} color={C.lime} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.lime, marginBottom: 4 }}>
                Review gate next
              </div>
              <div style={{ color: C.mute, fontSize: 12.5, lineHeight: 1.4 }}>
                When all keyframes are restyled, you&apos;ll land back on the review screen with the new images side-by-side with the originals. Omni Flash only fires after you hit <b style={{ color: C.text }}>Generate video →</b>.
              </div>
            </div>
          </div>
        )}

        {err && (
          <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: "rgba(209,73,91,.12)", border: `1px solid ${C.crimson}`, color: C.crimson, fontSize: 13 }}>
            {err}
          </div>
        )}
      </div>
    );
  }

  // Demo mode fallback (the old shape with proc.kf / proc.omni / proc.stitch)
  if (!proc) return null;
  const rows = [
    { label: "Cuts detected", state: "done" },
    { label: `Restyling keyframes · ${proc.kf}/${proc.steps?.find(s => s.key === "kf")?.total || 0}`,
      state: proc.kf >= (proc.steps?.find(s => s.key === "kf")?.total || 0) ? "done" : "run" },
    { label: route?.mode === "single" ? "Propagating with Omni Flash"
        : `Omni Flash · window ${proc.omni}/${proc.steps?.find(s => s.key === "omni")?.total || 0}`,
      state: proc.omni >= (proc.steps?.find(s => s.key === "omni")?.total || 0) ? "done" : (proc.kf > 0 ? "run" : "wait") },
    ...(route?.mode === "split" ? [{ label: "Restitching", state: proc.stitch ? "done" : "wait" }] : []),
  ];
  return (
    <div style={{ paddingTop: 30 }}>
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: C.lime, marginBottom: 8 }}>WORKING…</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 24px" }}>{route?.label} in progress</h2>
      <div style={{ display: "grid", gap: 2 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 14px", background: C.panel, borderRadius: 10, border: `1px solid ${C.line}`, opacity: r.state === "wait" ? 0.45 : 1 }}>
            <Icon state={r.state} />
            <span style={{ fontSize: 14, fontWeight: r.state === "run" ? 600 : 400 }}>{r.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MilestoneRow({ state, label }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
      background: C.panel, borderRadius: 10, border: `1px solid ${C.line}`,
      opacity: state === "wait" ? 0.45 : 1,
    }}>
      <Icon state={state} />
      <span style={{ fontSize: 13, fontWeight: state === "run" ? 600 : 400 }}>{label}</span>
    </div>
  );
}

function Icon({ state }) {
  if (state === "done") return <Check size={17} color={C.lime} strokeWidth={3} />;
  if (state === "run") return <Loader2 size={17} color={C.lime} style={{ animation: "spin 1s linear infinite" }} />;
  return <CircleDot size={16} color={C.mute} />;
}

/* ---- result ---------------------------------------------------------- */
function ResultView({ job, onReset }) {
  const edited = (job?.keyframes || []).filter((k) => (k.instruction || "").trim());
  const aspect = (job?.width && job?.height) ? `${job.width} / ${job.height}` : "16 / 9";
  const url = job?.result_url || "";
  return (
    <div style={{ paddingTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <div style={{ width: 22, height: 22, borderRadius: 999, background: C.lime, display: "grid", placeItems: "center" }}>
          <Check size={13} color={C.bg} strokeWidth={3} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Edit complete</h2>
      </div>

      <div style={{ background: C.panel, borderRadius: 14, border: `1px solid ${C.line}`, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ aspectRatio: aspect, background: "#000", position: "relative" }}>
          {url ? (
            <video
              data-testid="videoedit-result-video"
              src={url}
              controls
              autoPlay
              loop
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
            />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: C.mute, fontFamily: mono, fontSize: 12 }}>
              result.mp4 unavailable
            </div>
          )}
        </div>
        <div style={{ padding: "10px 14px", fontFamily: mono, fontSize: 11, color: C.mute, borderTop: `1px solid ${C.line}` }}>
          result.mp4 · {job?.duration ? `${job.duration.toFixed(1)}s` : "—"} · {edited.length} shot{edited.length === 1 ? "" : "s"} changed
        </div>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <a
          data-testid="videoedit-download-btn"
          href={url && job?.id
            ? `${API_BASE}/api/omni-flash-edit/${job.id}/download`
            : "#"}
          download={url ? "omni-flash-edit.mp4" : undefined}
          onClick={(e) => { if (!url) e.preventDefault(); }}
          style={{
            flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 8,
            background: url ? C.lime : C.panel2, color: url ? C.bg : C.mute,
            border: "none", borderRadius: 10, padding: "13px", fontWeight: 700, fontSize: 14,
            cursor: url ? "pointer" : "not-allowed", textDecoration: "none",
          }}>
          <Download size={16} /> Download
        </a>
        <button data-testid="videoedit-newedit-btn" onClick={onReset} style={{ display: "flex", alignItems: "center", gap: 8, background: C.panel, color: C.text, border: `1px solid ${C.line}`, borderRadius: 10, padding: "13px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
          <RotateCcw size={15} /> New edit
        </button>
      </div>
    </div>
  );
}

/* keyframe spin */
if (typeof document !== "undefined" && !document.getElementById("er-kf")) {
  const styleTag = document.createElement("style");
  styleTag.id = "er-kf";
  styleTag.textContent = "@keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}";
  document.head.appendChild(styleTag);
}
